import { Box, TextField, Typography } from "@mui/material";
import { DATE_INPUT_LANG } from "@/constants/dates";
import { useScheduleReconciliation } from "@/hooks/useSubscriptionSchedules";
import { card, Fact, money } from "./shared";

/**
 * The check at the top: what the schedules say GFG still owes in service, against what the books
 * say, as of a date — by default the last closed month end, the figure finance compares with
 * QuickBooks and the one that no longer changes. When the two disagree, it says what accounts for
 * the gap and links to the list.
 */
export default function ReconciliationStrip({
  asOf,
  asOfNote,
  onAsOfChange,
  onReview,
}: {
  asOf: string | undefined;
  /** Why this date is the default — shown under the picker. */
  asOfNote: string | null;
  onAsOfChange: (asOf: string) => void;
  onReview: () => void;
}) {
  const { data, isLoading } = useScheduleReconciliation(asOf);
  const differenceCents = Math.round(Number(data?.difference ?? 0) * 100);
  const unscheduled = data?.paymentsWithoutSchedule.length ?? 0;
  const unexplained = data?.unexplainedEntries.length ?? 0;
  const fullyExplained = data ? data.explained === data.difference : false;

  return (
    <Box component="section" aria-label="Check against the books" sx={{ ...card, mb: 3.5 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 4 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, minmax(0, 1fr))" },
            gap: 4,
            flex: "999 1 640px",
            minWidth: 0,
          }}
        >
          <Box>
            <TextField
              type="date"
              lang={DATE_INPUT_LANG}
              size="small"
              label="As of"
              value={data?.asOf ?? asOf ?? ""}
              onChange={(e) => e.target.value && onAsOfChange(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ width: 180 }}
            />
            {asOfNote && (
              <Typography sx={{ fontSize: 12, color: "var(--color-graphite)", mt: 0.5 }}>
                {asOfNote}
              </Typography>
            )}
          </Box>
          <Fact label="Still owed, per schedules">
            {isLoading ? "…" : money(data?.scheduleRemaining)}
          </Fact>
          <Fact label="Still owed, per the books">
            {isLoading ? "…" : money(data?.ledgerBalance)}
          </Fact>
          <Fact
            label="Difference"
            tone={isLoading ? undefined : differenceCents === 0 ? "ok" : "warn"}
          >
            {isLoading ? "…" : money(data?.difference)}
          </Fact>
        </Box>
        {!isLoading && differenceCents !== 0 && (
          <Box
            sx={{
              flex: "1 1 260px",
              background: "var(--color-good-wine-bg)",
              borderRadius: "8px",
              px: 2,
              py: 1.5,
              fontSize: 13,
            }}
          >
            <Typography
              component="span"
              sx={{ fontSize: 13, fontWeight: 700, color: "var(--color-good-wine)" }}
            >
              {[
                unscheduled
                  ? `${unscheduled} payment${unscheduled === 1 ? "" : "s"} with no schedule`
                  : null,
                unexplained
                  ? `${unexplained} unexplained entr${unexplained === 1 ? "y" : "ies"}`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ") || "Unexplained difference"}
              .
            </Typography>{" "}
            {fullyExplained
              ? "They explain the whole difference."
              : "They do not explain all of it."}
            <Box
              component="button"
              type="button"
              onClick={onReview}
              sx={{
                display: "block",
                mt: 0.75,
                p: 0,
                border: "none",
                background: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: 700,
                color: "var(--color-rodeo-black)",
                textDecoration: "underline",
                textUnderlineOffset: 3,
              }}
            >
              Review differences →
            </Box>
          </Box>
        )}
      </Box>
      {data && (
        <Typography sx={{ fontSize: 13, color: "var(--color-rodeo-black)", mt: 2 }}>
          {differenceCents === 0
            ? `The schedules and the books agree: GFG still owes ${money(data.ledgerBalance)} in service.`
            : `The schedules say GFG still owes ${money(data.scheduleRemaining)} in service; the books say ${money(data.ledgerBalance)}. They should match.`}
        </Typography>
      )}
    </Box>
  );
}
